/**
 * Gallery axis strategy for 'index' mode.
 *
 * Displays one row per media index, with step and run controlled via sliders.
 */

import {getStepFromSliderStep} from '../../utils/stepUtils';
import {GridRow, SlotRegistry, TransformInput} from '../types';
import {createCellsForMediaKeys} from '../utils/cells';
import {caption, label, stepSlider} from '../utils/slots';
import {GalleryAxisStrategy} from './GalleryAxisStrategy';

export class IndexAxisStrategy implements GalleryAxisStrategy {
  getAxisValues(input: TransformInput, maxItems?: number): number[] {
    const {stepMeta} = input;

    const indices = stepMeta.indices;

    // Apply maxGalleryItems limit to index range
    const indicesToProcess =
      maxItems && indices.length > maxItems
        ? indices.slice(0, maxItems)
        : indices;

    return indicesToProcess;
  }

  createRows(input: TransformInput, axisValues: number[]): GridRow[] {
    const {runs, stepMeta, config, allMasks} = input;
    const {mediaKeys} = config;
    const currentStep = input.currentStep ?? 0;

    if (!mediaKeys || mediaKeys.length === 0) {
      return [];
    }

    // Use first run for index axis
    const run = runs[0];
    if (!run) {
      return [];
    }

    const runStepMeta = stepMeta.runs[0];

    // Snap the current step to this run's available steps
    // IMPORTANT: Always snap for tile lookup, regardless of snapToExistingStep
    // The snapToExistingStep setting only affects slider display, not tile lookup
    const {step: snappedStep, sliderStep} = getStepFromSliderStep(
      runStepMeta,
      currentStep,
      true // Always snap for tile lookup
    );

    const rows: GridRow[] = [];

    for (const idx of axisValues) {
      const cells = createCellsForMediaKeys(
        run,
        snappedStep,
        idx,
        mediaKeys,
        config,
        allMasks,
        sliderStep
      );

      if (cells.length > 0) {
        rows.push({
          cells,
          rowKey: `index-${idx}`,
          rowLabel: `Image ${idx}`,
        });
      }
    }

    return rows;
  }

  buildSlotRegistry(): SlotRegistry {
    // Index axis: indices shown on each tile, step and run are controlled
    return {
      'tile-header': [label('index')],
      'tile-footer': [caption()],
      'content-footer': [stepSlider()],
    };
  }
}
